/**
 * @lines98/client/components/panel-row — примитив «строка на плашке».
 *
 * ЗАЧЕМ (owner-directive 2026-06-04 «делаем примитивы»; «не наезжай by design»).
 * Плашка-строка [главный слот | контрол] — базовый элемент списков (настройки,
 * лидерборд, drill-входы). Ключевое правило кирпичика: **плашка САМА считает свою
 * толщину от контрола** (толщина ≥ контрол + воздух), поэтому контрол физически НЕ
 * может вылезти за края плашки. Это owner-rule verbatim (2026-06-04): «не помещается
 * — делай полосы толще, а не кнопки уже», зашитый в кирпичик раз и навсегда, а не
 * вспоминаемый на каждом экране. Модель «ствол → ветки»: экран/компонент монтирует
 * panel-row и наполняет слоты, не переобъявляя геометрию.
 *
 * Размеры — токенами (--btn-h / --m / --r-sm), без хардкода (CSS-гейт). Тактильный
 * floor живёт в --btn-h; толщина = calc(--btn-h + 2·верт-воздух).
 *
 * Два режима:
 *   - обычный (div): главный слот + контрол справа (строка настройки/стата);
 *   - interactive (button): вся строка кликабельна (drill-вход «Оформление доски»),
 *     контрол-слот = декоративный трейлинг (шеврон).
 *
 * CSS — co-located `panel-row.css`. Специализации (setting-row и т.п.) наполняют
 * слоты и добавляют только свой внешний вид.
 */

import './panel-row.css';

export interface PanelRowOptions {
  /** Главный слот (слева): текст-колонка / label / произвольный контент. */
  readonly main: HTMLElement;
  /** Контрол справа (toggle / иконка / шеврон). Опционально. */
  readonly control?: HTMLElement;
  /** Вся строка — кликабельная кнопка (drill-вход). По умолчанию div. */
  readonly interactive?: boolean;
  /** Обработчик клика (для interactive). */
  readonly onClick?: (e: MouseEvent) => void;
  /** Доп. класс(ы) экрана-потребителя (внешний вид/позиционирование). */
  readonly className?: string;
  /** ARIA role (напр. 'listitem'). */
  readonly role?: string;
  /** ARIA label (для interactive без явной подписи). */
  readonly ariaLabel?: string;
}

export interface PanelRowHandle {
  /** Корневой элемент строки (div или button). */
  readonly element: HTMLElement;
}

/** Создать плашку-строку. Возвращает handle с `.element`. */
export function createPanelRow(opts: PanelRowOptions): PanelRowHandle {
  const root = document.createElement(opts.interactive ? 'button' : 'div');
  root.className = 'panel-row';
  if (opts.interactive) {
    root.classList.add('panel-row--interactive');
    (root as HTMLButtonElement).type = 'button';
  }
  if (opts.className) root.classList.add(...opts.className.split(/\s+/).filter(Boolean));
  if (opts.role) root.setAttribute('role', opts.role);
  if (opts.ariaLabel) root.setAttribute('aria-label', opts.ariaLabel);

  const main = document.createElement('div');
  main.className = 'panel-row-main';
  main.appendChild(opts.main);
  root.appendChild(main);

  if (opts.control) {
    const control = document.createElement('div');
    control.className = 'panel-row-control';
    control.appendChild(opts.control);
    root.appendChild(control);
  }

  if (opts.onClick) {
    const cb = opts.onClick;
    root.addEventListener('click', (e) => cb(e as MouseEvent));
  }

  return { element: root };
}
